import { Injectable, inject } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { HttpParams, HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { debounceTime, switchMap, map, first, catchError } from 'rxjs/operators';
import { Role, UserProfile } from '../../models';

@Injectable({
  providedIn: 'root',
})
export class DashboardUserService {
  private http = inject(HttpClient);

  configUrl = environment.apiUrl;

  public getMoreUsers = new Subject<{
    limit: number,
    offset: number,
    searchQuery: string,
    role: string[],
    isPro?: boolean,
    exactSearch?: boolean,
  }>();
  public getMoreAccounts = new Subject<{
    limit: number,
    offset: number,
    searchQuery: string,
    sort: string,
    periodStart?: Date,
    periodEnd?: Date,
  }>();

  exportFilteredUsers(filters): Observable<ArrayBuffer> {
    const route = `${this.configUrl}exploreUsers`;
    const params = new HttpParams({
      fromObject: {
        format: 'csv',
        searchQuery: filters?.searchQuery ? encodeURIComponent(filters?.searchQuery?.toString()) : '',
        ...filters.role ? filters.role?.filter( (r) => !!r)?.reduce( (acc, r, i) => ({[`role[${i}]`]: r, ...acc}), {}) : '',
      },
    });
    return this.http.get(route, {params, responseType: 'arraybuffer'}).pipe(
      map((file: ArrayBuffer) => file),
    );
  }

  getUserList() {
    const route = `${this.configUrl}exploreUsers`;
    return this.getMoreUsers.pipe(
      // wait 300ms after each keystroke before considering the term
      debounceTime(600),
      // switch to new search observable each time the term changes
      switchMap( ({limit, offset, searchQuery, role, isPro, exactSearch}) => {
        const params = new HttpParams({
          fromObject: {
            limit: limit.toString(),
            offset: offset.toString(),
            searchQuery: encodeURIComponent(searchQuery.toString()),
            ...role?.filter( (r) => !!r)?.reduce( (acc, r, i) => ({[`role[${i}]`]: r, ...acc}), {}),
            isPro,
            exactSearch,
          },
        });
        return this.http.get<{rows: UserProfile[], count: number}>(route, {params});
      }),
    );
  }

  getAccountList() {
    const route = `${this.configUrl}exploreAccounts`;
    return this.getMoreAccounts.pipe(
      // wait 300ms after each keystroke before considering the term
      debounceTime(600),
      // switch to new search observable each time the term changes
      switchMap( ({limit, offset, searchQuery, sort, periodStart, periodEnd}) => {
        const params = new HttpParams({
          fromObject: {
            limit,
            offset,
            searchQuery: encodeURIComponent(searchQuery.toString()),
            sort,
            periodStart: periodStart?.toISOString(),
            periodEnd: periodEnd?.toISOString(),
          },
        });
        return this.http.get<{rows: UserProfile[], count: {count: number}[]}>(route, {params});
      }),
    );
  }

  getUserBasicInfo(userId: number): Observable<UserProfile> {
    if(userId){
      const route = `${this.configUrl}organizer/userInfo/${userId}`;
      return this.http.get<UserProfile>(route).pipe(
        first(),
        catchError(() => of({id: userId, firstname: 'Masqué - N\'est pas un utilisateur de votre événement'} as UserProfile)),
      );
    }
    return of({id: 0, firstname: 'Serveur Reelax', email: 'tech@reelax-tickets.com', role: Role.Admin} as UserProfile);
  }

}
